import { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";

function toHex(char) {
  return char.codePointAt(0).toString(16).padStart(5, "0");
}

const CDN = "https://cdn.jsdelivr.net/gh/KanjiVG/kanjivg@master/kanji";

export default function StrokeOrderSVG({ char, size = 320, replayKey = 0 }) {
  const [svgHtml, setSvgHtml] = useState(null);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    setSvgHtml(null);
    fetch(`${CDN}/${toHex(char)}.svg`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error("nf"))))
      .then((txt) => {
        if (cancelled) return;
        // XSS protection: sanitize before dangerouslySetInnerHTML
        const raw = txt.replace(/<\?xml[^>]*\?>/i, "").replace(/<!DOCTYPE[\s\S]*?\]>/i, "").replace(/<!--[\s\S]*?-->/g, "");
        const clean = DOMPurify.sanitize(raw, {
          USE_PROFILES: { svg: true, svgFilters: true },
          FORBID_TAGS: ["script", "foreignObject"],
          FORBID_ATTR: ["onload", "onerror", "onclick"],
        });
        setSvgHtml(clean);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [char]);

  useEffect(() => {
    if (!svgHtml || !containerRef.current) return;
    const root = containerRef.current;
    const svg = root.querySelector("svg");
    if (!svg) return;

    svg.setAttribute("width", size);
    svg.setAttribute("height", size);
    svg.style.overflow = "visible";
    // Hide stroke numbers
    root.querySelectorAll('text, [id*="StrokeNumbers"]').forEach((el) => { el.style.display = "none"; });

    const paths = Array.from(svg.querySelectorAll("path"));
    // Reset styles + measure
    const lens = paths.map((p) => {
      p.style.fill = "none";
      p.style.stroke = "#1E1F24";
      p.style.strokeWidth = "4";
      p.style.strokeLinecap = "round";
      p.style.strokeLinejoin = "round";
      p.style.transition = "none";
      const len = p.getTotalLength ? p.getTotalLength() : 400;
      p.style.strokeDasharray = String(len);
      p.style.strokeDashoffset = String(len);
      return len;
    });

    // Force reflow so browser registers initial state
    // eslint-disable-next-line no-unused-expressions
    svg.getBoundingClientRect();

    // Animate sequentially
    const perStroke = 650;
    let acc = 0;
    paths.forEach((p, i) => {
      const dur = Math.min(1200, Math.max(400, lens[i] * 3));
      setTimeout(() => {
        p.style.transition = `stroke-dashoffset ${dur}ms cubic-bezier(.55,.08,.35,1)`;
        p.style.strokeDashoffset = "0";
        // Tint the drawing tip briefly
        p.style.stroke = "#FF80AB";
        setTimeout(() => { p.style.stroke = "#1E1F24"; }, dur + 30);
      }, acc);
      acc += Math.max(perStroke - 150, dur - 200);
    });
  }, [svgHtml, size, replayKey]);

  if (error) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <span className="font-jp text-[10rem] sm:text-[13rem] leading-none brush-draw text-ink" key={replayKey}>{char}</span>
      </div>
    );
  }

  if (!svgHtml) {
    return (
      <div className="flex items-center justify-center w-full h-full opacity-40 animate-pulse">
        <span className="font-jp text-[10rem] sm:text-[13rem] leading-none text-ink/30">{char}</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      key={`${char}-${replayKey}`}
      className="w-full h-full flex items-center justify-center [&_svg]:max-w-full [&_svg]:max-h-full"
      dangerouslySetInnerHTML={{ __html: svgHtml }}
    />
  );
}
